import { collection, getDocs, query } from 'firebase/firestore';
import { db } from '../../data/base';

/**
 * Funzione per ordinare le taglie in modo logico
 * Gestisce valori numerici e alfanumerici con logica intelligente
 * @param {Array} sizes - Array di oggetti size con proprietà label
 * @returns {Array} Array ordinato
 */
const sortSizes = (sizes) => {
    return sizes.sort((a, b) => {
        const labelA = a.label ? a.label.toString().trim() : '';
        const labelB = b.label ? b.label.toString().trim() : '';

        // Funzione helper per estrarre numero e parte letterale
        const parseSize = (str) => {
            // Rimuovi spazi e converti in maiuscolo per confronto consistente
            const cleanStr = str.replace(/\s+/g, '').toUpperCase();

            // Pattern per catturare numero seguito da lettere (es: "52MM", "48XL", "42S")
            const numericMatch = cleanStr.match(/^(\d+(?:\.\d+)?)([A-Z]*)$/);
            if (numericMatch) {
                return {
                    type: 'numeric',
                    number: parseFloat(numericMatch[1]),
                    suffix: numericMatch[2] || ''
                };
            }

            // Pattern per dimensioni con X (es: "ONE SIZE", "XS", "S", "M", "L", "XL", "XXL")
            const sizeMatch = cleanStr.match(/^(XXX?L|XX?L|XL|L|M|S|XS|XXX?S)$/);
            if (sizeMatch) {
                // Ordine standard per le taglie clothing
                const sizeOrder = {
                    'XXXS': 1, 'XXS': 2, 'XS': 3, 'S': 4, 'M': 5, 'L': 6,
                    'XL': 7, 'XXL': 8, 'XXXL': 9
                };
                return {
                    type: 'clothing',
                    order: sizeOrder[sizeMatch[1]] || 999,
                    original: sizeMatch[1]
                };
            }

            // Gestione speciale per "ONE SIZE"
            if (cleanStr.includes('ONESIZE') || cleanStr.includes('ONE_SIZE') || cleanStr === 'OS') {
                return { type: 'onesize', order: 1000 };
            }

            // Gestione per taglie bambini (KIDS, CHILD, ecc.)
            if (cleanStr.includes('KIDS') || cleanStr.includes('CHILD') || cleanStr.includes('BABY')) {
                return { type: 'kids', order: 10000, original: cleanStr };
            }

            // Tutto il resto viene ordinato alfabeticamente
            return { type: 'other', original: cleanStr };
        };

        const parsedA = parseSize(labelA);
        const parsedB = parseSize(labelB);

        // Prima ordina per tipo
        const typeOrder = { 'numeric': 1, 'clothing': 2, 'onesize': 3, 'kids': 4, 'other': 5 };
        const typeCompare = (typeOrder[parsedA.type] || 999) - (typeOrder[parsedB.type] || 999);

        if (typeCompare !== 0) return typeCompare;

        // All'interno dello stesso tipo, applica logica specifica
        switch (parsedA.type) {
            case 'numeric':
                // Prima ordina per numero, poi per suffisso
                if (parsedA.number !== parsedB.number) {
                    return parsedA.number - parsedB.number;
                }
                return parsedA.suffix.localeCompare(parsedB.suffix);

            case 'clothing':
                return parsedA.order - parsedB.order;

            case 'onesize':
                return 0; // Sono uguali

            case 'kids':
            case 'other':
            default:
                return parsedA.original.localeCompare(parsedB.original);
        }
    });
};

/**
 * Recupera i tag da Firestore e li organizza per categorie (linee, generi, tipi di modello)
 * @returns {Promise<Object>} Un oggetto contenente le linee, i generi e i tipi di modello
 */
export const fetchTagsData = async () => {
    try {
        // Set per memorizzare tutti gli ID dei tag per gender e model type
        const allTagsIds = new Set();
        // 🎯 NUOVO: Set per memorizzare i tag size e frame color direttamente dai prodotti
        const allSizeTagsIds = new Set();
        const allFrameColorTagsIds = new Set();

        // Mappa per memorizzare le associazioni brand-linee
        const brandToLinesMap = {};

        // Recupera tutti i prodotti per estrarre i tag
        const productsCollection = collection(db, "Products");
        const productsSnapshot = await getDocs(query(productsCollection));

        // Passo 1: Inizializza la mappa brand-linee
        productsSnapshot.docs.forEach(doc => {
            const productData = doc.data();
            const brandId = productData.mainBrandRef;

            if (brandId && !brandToLinesMap[brandId]) {
                brandToLinesMap[brandId] = new Set();
            }
        });

        // Passo 2: Raccoglie tutti gli ID dei tag dai prodotti
        productsSnapshot.docs.forEach(doc => {
            const productData = doc.data();

            // Raccoglie list_tags per gender, model type e frame shapes
            if (productData.list_tags && Array.isArray(productData.list_tags)) {
                productData.list_tags.forEach(tagId => {
                    allTagsIds.add(tagId);
                });
            }

            // 🎯 NUOVO: Raccoglie list_size_tags per sizes
            if (productData.list_size_tags && Array.isArray(productData.list_size_tags)) {
                productData.list_size_tags.forEach(tagId => {
                    allSizeTagsIds.add(tagId);
                });
            }

            // 🎯 NUOVO: Raccoglie list_frame_color_tags per frame colors
            if (productData.list_frame_color_tags && Array.isArray(productData.list_frame_color_tags)) {
                productData.list_frame_color_tags.forEach(tagId => {
                    allFrameColorTagsIds.add(tagId);
                });
            }
        });

        // ❌ RIMOSSO: Non raccogliamo più dalle varianti
        // Converti i Set in array
        const allTagsArray = Array.from(allTagsIds);
        const allSizeTagsArray = Array.from(allSizeTagsIds);
        const allFrameColorTagsArray = Array.from(allFrameColorTagsIds);

        // Recupera tutti i MainBrands per verificare i catalogRef
        const mainBrandsCollection = collection(db, "MainBrands");
        const mainBrandsSnapshot = await getDocs(query(mainBrandsCollection));

        // Crea un Set con tutti gli ID dei MainBrands per controllo veloce
        const mainBrandsIds = new Set();
        mainBrandsSnapshot.docs.forEach(doc => {
            mainBrandsIds.add(doc.id);
        });

        // Recupera tutti i tag
        const tagsCollection = collection(db, "Tags");
        const tagsSnapshot = await getDocs(tagsCollection);

        // Array per memorizzare i dati filtrati
        const lineDetails = [];
        const genderDetails = [];
        const sizeDetails = [];
        const frameColorDetails = [];
        const frameShapeDetails = [];

        // Mappa per memorizzare le linee per brand
        const brandLinesMap = {};

        // Mappa per raggruppare i tag simili di Tipologia
        const modelTypeGroups = {
            "Optical frames": {
                ids: [],
                patterns: ["Occhiali vista", "OCCHIALI VISTA", "Optical frames", "OPTICAL FRAMES"]
            },
            "Sunglasses frames": {
                ids: [],
                patterns: ["Occhiali sole", "OCCHIALI SOLE", "Sunglass frames", "SUNGLASS FRAMES", "SUNGLASSES FRAMES"]
            },
            "Optical frames with Clip-on": {
                ids: [],
                patterns: ["OPT + CLIP-ON", "Optical frames + Clip-on", "OPTICAL FRAMES + CLIP-ON"]
            }
        };

        // Array per memorizzare i tipi di modello (Tipologia) raggruppati
        const modelTypeDetails = [];

        tagsSnapshot.docs.forEach(tagDoc => {
            const tagData = tagDoc.data();
            const tagId = tagDoc.id;

            // Se è una linea, aggiungi solo se non finisce con "REV" E se il catalogRef è presente in MainBrands
            if (tagData.group === "Line") {
                const lineName = tagData.name || 'Unnamed Line';

                // Verifica se il nome della linea finisce con "REV"
                if (!lineName.endsWith('REV')) {
                    // Verifica se il catalogRef è presente in MainBrands
                    if (tagData.catalogRef && mainBrandsIds.has(tagData.catalogRef)) {
                        // Aggiungi la linea solo se il catalogRef è presente in MainBrands
                        lineDetails.push({
                            value: tagId,
                            label: lineName,
                            brandId: tagData.catalogRef // Usa catalogRef come brandId per compatibilità
                        });

                        // Aggiungi la linea alla mappa brand-linee
                        if (!brandLinesMap[tagData.catalogRef]) {
                            brandLinesMap[tagData.catalogRef] = [];
                        }
                        brandLinesMap[tagData.catalogRef].push({
                            value: tagId,
                            label: lineName
                        });

                        // Aggiorna anche brandToLinesMap
                        if (brandToLinesMap[tagData.catalogRef]) {
                            brandToLinesMap[tagData.catalogRef].add(tagId);
                        }
                    }
                }
            }

            // Se è un gender che viene usato nei prodotti
            if (tagData.group === "Gender" && allTagsArray.includes(tagId)) {
                genderDetails.push({
                    value: tagId,
                    label: tagData.name || 'Unnamed Gender'
                });
            }

            // Se è un tag di tipologia che viene usato nei prodotti
            if (tagData.group === "Tipologia" && allTagsArray.includes(tagId)) {
                const tagName = tagData.name || '';

                // Controlla se il tag appartiene a uno dei gruppi definiti
                let matched = false;
                for (const [groupName, groupData] of Object.entries(modelTypeGroups)) {
                    if (groupData.patterns.some(pattern => tagName.includes(pattern))) {
                        // Aggiungi l'ID del tag al gruppo corrispondente
                        modelTypeGroups[groupName].ids.push(tagId);
                        matched = true;
                        break;
                    }
                }

                // Se il tag non corrisponde a nessun gruppo, aggiungilo come tipo individuale
                if (!matched) {
                    modelTypeDetails.push({
                        value: tagId,
                        label: tagName || 'Unnamed Type'
                    });
                }
            }

            // 🎯 NUOVO: Size utilizzando list_size_tags dai prodotti
            if (tagData.group === "Size" && allSizeTagsArray.includes(tagId)) {
                sizeDetails.push({
                    value: tagId,
                    label: tagData.name || 'Unnamed Size'
                });
            }

            // 🎯 NUOVO: Frame Color utilizzando list_frame_color_tags dai prodotti
            if (tagData.group === "Frame Color" && allFrameColorTagsArray.includes(tagId)) {
                frameColorDetails.push({
                    value: tagId,
                    label: tagData.name || 'Unnamed Frame Color'
                });
            }

            // 🎯 MODIFICATO: Frame Shape utilizzando list_tags dai prodotti (group "Forma")
            if (tagData.group === "Forma" && allTagsArray.includes(tagId)) {
                frameShapeDetails.push({
                    value: tagId,
                    label: tagData.name || 'Unnamed Frame Shape'
                });
            }
        });

        // Aggiungi i gruppi di model types all'array modelTypeDetails
        Object.entries(modelTypeGroups).forEach(([groupName, groupData]) => {
            // Aggiungi solo i gruppi che hanno almeno un tag
            if (groupData.ids.length > 0) {
                modelTypeDetails.push({
                    value: JSON.stringify(groupData.ids), // Salva tutti gli ID come JSON string
                    label: groupName,
                    isGroup: true // Flag per indicare che questo è un gruppo
                });
            }
        });

        // Ordina i model types per mettere i gruppi prima
        modelTypeDetails.sort((a, b) => {
            if (a.isGroup && !b.isGroup) return -1;
            if (!a.isGroup && b.isGroup) return 1;
            return a.label.localeCompare(b.label);
        });

        // 🎯 NUOVO: Ordina le taglie utilizzando la funzione di ordinamento logico
        const sortedSizes = sortSizes(sizeDetails);

        return {
            lines: lineDetails.sort((a, b) => (a.label || '').localeCompare(b.label || '')),
            genders: genderDetails.sort((a, b) => (a.label || '').localeCompare(b.label || '')),
            modelTypes: modelTypeDetails.sort((a, b) => (a.label || '').localeCompare(b.label || '')),
            sizes: sortedSizes, // 🎯 Già ordinati con logica custom
            frameColors: frameColorDetails.sort((a, b) => (a.label || '').localeCompare(b.label || '')),
            frameShapes: frameShapeDetails.sort((a, b) => (a.label || '').localeCompare(b.label || '')),
            brandLinesMap,
            brandToLinesMap: Object.fromEntries(
                Object.entries(brandToLinesMap).map(([brandId, lineSet]) => [
                    brandId,
                    Array.from(lineSet).sort((a, b) => (a.label || '').localeCompare(b.label || ''))
                ])
            )
        };
    } catch (error) {
        console.error("Error fetching tags data:", error);
        throw error;
    }
};

/**
 * Recupera i brand principali da Firestore
 * @returns {Promise<Array>} Un array di oggetti brand
 */
export const fetchMainBrands = async () => {
    try {
        const brandsCollection = collection(db, "MainBrands");
        const brandsSnapshot = await getDocs(query(brandsCollection));

        if (brandsSnapshot.empty) {
            console.warn("No brands found in Firestore");
            return [];
        }

        const brands = brandsSnapshot.docs.map(doc => {
            const data = doc.data();

            // Controlla tutti i campi disponibili per trovare il nome del brand
            let brandName = 'Unnamed Brand';
            if (data.nameBrand) brandName = data.nameBrand;
            else if (data.name) brandName = data.name;
            else if (data.brandName) brandName = data.brandName;
            else if (data.label) brandName = data.label;

            // Sanitizza i dati per rimuovere oggetti non serializzabili
            const sanitizedData = {};
            Object.keys(data).forEach(key => {
                const value = data[key];
                // Includi solo tipi di dati serializzabili
                if (value !== null && value !== undefined) {
                    const valueType = typeof value;
                    if (valueType === 'string' || valueType === 'number' || valueType === 'boolean') {
                        sanitizedData[key] = value;
                    } else if (Array.isArray(value)) {
                        // Per gli array, controlla che tutti gli elementi siano serializzabili
                        sanitizedData[key] = value.filter(item =>
                            typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean'
                        );
                    } else if (valueType === 'object' && value.constructor === Object) {
                        // Per oggetti semplici, controlla ricorsivamente
                        try {
                            JSON.stringify(value);
                            sanitizedData[key] = value;
                        } catch (error) {
                            console.warn(`Skipping non-serializable field ${key} in brand ${doc.id}:`, error);
                        }
                    } else if (value.toDate && typeof value.toDate === 'function') {
                        // Converte i Timestamp di Firestore in stringhe ISO
                        sanitizedData[key] = value.toDate().toISOString();
                    }
                    // Ignora funzioni, simboli e altri tipi non serializzabili
                }
            });

            return {
                id: doc.id,
                ...sanitizedData,
                value: doc.id,
                label: brandName
            };
        });

        return brands.sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    } catch (error) {
        console.error("Error fetching main brands:", error);
        throw error;
    }
};
